/**
 * Safe version of useAuthState that provides better error handling
 * and fallback behavior for components that might be rendered outside
 * the AuthStateProvider context.
 */

import { useContext } from 'react';
import { useAuthState } from './useAuthState';
import { logger } from '@/shared/utils/logger';
interface SafeAuthStateOptions {
  /**
   * Component name for better error reporting
   */
  componentName?: string;
  
  /**
   * Whether to throw errors in development (default: true)
   */
  throwInDev?: boolean;
  
  /**
   * Whether to provide fallback values in production (default: true)
   */
  fallbackInProd?: boolean;
}

/**
 * Safe wrapper around useAuthState that provides better error handling
 * and development experience.
 */
export const useAuthStateSafe = (options: SafeAuthStateOptions = {}) => {
  const {
    componentName = 'Unknown Component',
    throwInDev = true,
    fallbackInProd = true
  } = options;

  try {
    return useAuthState();
  } catch (error) {
    const errorMessage = `${componentName} is trying to use useAuthState outside of AuthStateProvider`;
    
    if (process.env.NODE_ENV === 'development') {
      logger.group('🚨 AuthState Provider Error');
      logger.error(errorMessage);
      logger.error('Component:', componentName);
      logger.error('Stack trace:', error);
      logger.groupCollapsed('💡 How to fix this:');
      logger.debug('1. Make sure your App.tsx includes <AuthStateProvider>');
      logger.debug('2. Verify the component is rendered within the provider tree');
      logger.debug('3. Check for conditional rendering that might bypass the provider');
      logger.groupEnd();
      logger.groupEnd();
      
      if (throwInDev) {
        throw error;
      }
    }
    
    if (fallbackInProd && process.env.NODE_ENV === 'production') {
      logger.warn(`${errorMessage} - using fallback values`);
      return {
        user: null,
        session: null,
        profile: null,
        roles: [],
        isLoading: false,
        hasRole: () => false,
      };
    }
    
    throw error;
  }
};

/**
 * Hook to check if AuthStateProvider is available in the current context
 */
export const useAuthStateAvailable = (): boolean => {
  try {
    useAuthState();
    return true;
  } catch {
    return false;
  }
};